import axios from 'axios';

// Development mode flag - toggle to use mock or real database connections
// Set to false to use the real database
const IS_DEV_MODE = false;

const API_URL = process.env.REACT_APP_API_URL || 'https://api.aajkyabanega.com/api';

// Mock users for development
const mockUsers = [
  {
    id: '1',
    name: 'Admin User',
    username: 'admin',
    email: 'admin@example.com',
    password: 'admin123',
    role: 'admin',
    preferences: {
      dietaryType: ['non-veg'],
      mealTypes: ['breakfast', 'lunch', 'dinner'],
      cuisineStyles: ['healthy', 'indian']
    }
  },
  {
    id: '2',
    name: 'Regular User',
    username: 'user',
    email: 'user@example.com',
    password: 'user123',
    role: 'user',
    preferences: {
      dietaryType: ['veg'],
      mealTypes: ['breakfast', 'dinner'],
      cuisineStyles: ['healthy']
    }
  }
];

// Mock JWT token generation
const generateMockToken = (user) => {
  return btoa(JSON.stringify({
    sub: user.id,
    email: user.email,
    role: user.role,
    exp: new Date().getTime() + 24 * 60 * 60 * 1000, // 24 hours
  }));
};

const authAPI = {
  login: async (credentials) => {
    // Use mock authentication in development mode
    if (IS_DEV_MODE) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          // Allow login with either username or email
          const user = mockUsers.find(u => 
            (u.email === credentials.email || u.username === credentials.email) && 
            u.password === credentials.password
          );
          
          if (user) {
            const token = generateMockToken(user);
            localStorage.setItem('token', token);
            localStorage.setItem('mockUser', JSON.stringify(user));
            
            resolve({
              data: {
                token,
                user: {
                  id: user.id,
                  name: user.name,
                  email: user.email,
                  role: user.role,
                  preferences: user.preferences
                }
              }
            });
          } else {
            reject({
              response: {
                status: 401,
                data: { message: 'Invalid email or password' }
              }
            });
          }
        }, 500); // Simulate network delay
      });
    }
    
    // Real API call for production
    const response = await axios.post(`${API_URL}/auth/login`, credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response;
  },

  register: async (userData) => {
    // Use mock authentication in development mode
    if (IS_DEV_MODE) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          // Check if username is provided
          if (!userData.username) {
            reject({
              response: {
                status: 400,
                data: { message: 'Username is required' }
              }
            });
            return;
          }
          
          // Check if username is already taken
          const existingUser = mockUsers.find(u => 
            u.username === userData.username || 
            u.email === userData.email
          );
          
          if (existingUser) {
            reject({
              response: {
                status: 400,
                data: { 
                  message: existingUser.email === userData.email 
                    ? 'Email already in use' 
                    : 'Username already taken'
                }
              }
            });
            return;
          }
          
          // Create new mock user
          const newUser = {
            id: String(mockUsers.length + 1),
            name: userData.name,
            username: userData.username,
            email: userData.email,
            password: userData.password,
            role: 'user',
            preferences: {
              dietaryType: userData.dietaryPreferences || [],
              mealTypes: ['breakfast', 'lunch', 'dinner'],
              cuisineStyles: ['healthy']
            }
          };
          
          // Add to mock users (this would be lost on refresh but works for demo)
          mockUsers.push(newUser);
          
          // Generate token and save user
          const token = generateMockToken(newUser);
          localStorage.setItem('token', token);
          localStorage.setItem('mockUser', JSON.stringify(newUser));
        
          resolve({
            data: {
              token,
              user: {
                id: newUser.id,
                name: newUser.name,
                username: newUser.username,
                email: newUser.email,
                role: newUser.role,
                preferences: newUser.preferences
              }
            }
          });
        }, 800); // Simulate network delay
      });
    }
    
    // Real API call for production
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response;
  },

  logout: () => {
    localStorage.removeItem('token');
  },

  getCurrentUser: async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    // Use mock authentication in development mode
    if (IS_DEV_MODE) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const storedUser = localStorage.getItem('mockUser');
          if (storedUser) {
            const user = JSON.parse(storedUser);
            resolve({
              id: user.id,
              name: user.name,
              username: user.username,
              email: user.email,
              role: user.role,
              preferences: user.preferences
            });
          } else {
            resolve(null);
          }
        }, 300);
      });
    }

    // Real API call for production
    const response = await axios.get(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// Add axios interceptor for JWT
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export { authAPI };
